import React from "react";
import { getCardColor2, formatName } from "../../../../../helpers/helpers";
import { getInitials } from "../../utilities/helpers";
import { formatPhoneNumber } from "../../../../../helpers/helpers";
import LoadingButton from "@mui/lab/LoadingButton";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import EmployeeProfileEmptyState from "./EmployeeProfileEmptyState";

export default function ArchivedEmployeesTable(props) {
  const [searchInput, setSearchInput] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("desc"); // "desc" = newest first, "asc" = oldest first

  // Filter employees based on search
  const filteredEmployees = props.archivedEmployees?.filter((employee) => {
    const searchParts = searchInput?.toLowerCase()?.split(" ");
    return searchParts?.every(
      (part) =>
        employee?.first_name?.toLowerCase()?.includes(part) ||
        employee?.last_name?.toLowerCase()?.includes(part) ||
        employee?.email?.toLowerCase()?.includes(part)
    );
  });

  // Sort employees by deactivated date
  const sortedEmployees = filteredEmployees?.sort((a, b) => {
    const dateA = new Date(a.archived_at || a.updated_at || 0);
    const dateB = new Date(b.archived_at || b.updated_at || 0);
    if (sortOrder === "desc") {
      return dateB - dateA; // Newest first
    } else {
      return dateA - dateB; // Oldest first
    }
  });

  const isEmpty = !sortedEmployees || sortedEmployees.length === 0;

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
  };

  return (
    <div className="mt-4">
      {/* Search Bar */}
      <div className="px-6 mb-4">
        <p className="text-slate-500 text-sm">Search by name or email</p>
        <div className="w-[250px] relative mt-1">
          <div className="relative">
            <SearchIcon
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              style={{ fontSize: "18px" }}
            />
            <input
              className="w-full border border-gray-300 pl-9 pr-3 h-[40px] text-sm rounded-lg outline-none transition-all duration-200 focus:border focus:border-violet-700 focus:shadow-sm"
              type="text"
              placeholder="Search archived employees"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden">
              {isEmpty ? (
                <EmployeeProfileEmptyState
                  icon={searchInput ? SearchOffIcon : Inventory2OutlinedIcon}
                  title={
                    searchInput
                      ? "No matching employees"
                      : "No archived employees"
                  }
                  description={
                    searchInput
                      ? `No archived employees match "${searchInput}". Try a different name or email.`
                      : "Archived employees will show up here. Archiving takes someone off schedules and the current roster, and keeps their history."
                  }
                />
              ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="w-[4%] pl-6 pr-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      <span className="sr-only">Avatar</span>
                    </th>
                    <th
                      scope="col"
                      className="w-[8%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      First
                    </th>
                    <th
                      scope="col"
                      className="w-[10%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Last
                    </th>
                    <th
                      scope="col"
                      className="w-[18%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Contact
                    </th>
                    <th
                      scope="col"
                      className="w-[14%] px-3 py-3.5 text-left text-sm text-slate-500 hover:text-slate-900 font-medium cursor-pointer transition-colors"
                      onClick={toggleSortOrder}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Deactivated</span>
                        {sortOrder === "desc" ? (
                          <ArrowDownwardIcon style={{ fontSize: "16px" }} />
                        ) : (
                          <ArrowUpwardIcon style={{ fontSize: "16px" }} />
                        )}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="w-[18%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Reason
                    </th>
                    <th
                      scope="col"
                      className="w-[8%] px-3 py-3.5 text-left text-sm text-slate-500 font-medium"
                    >
                      Rehire?
                    </th>
                    <th
                      scope="col"
                      className="w-[10%] relative py-3.5 pl-3 pr-6 text-center text-sm font-medium"
                    >
                      {/* Actions */}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {sortedEmployees?.map((person) => {
                    const avatarCardColor = person.primary_role_id
                      ? getCardColor2(
                          person.primary_role_id,
                          props.roleNames,
                          false
                        )
                      : "bg-slate-500";

                  const hasToastLink = Boolean(
                    person.pos_store_team_member_uuid ||
                      person.external_toast_employee_guid
                  );

                  return (
                    <tr key={person.id} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap pl-6 pr-3 py-4">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${avatarCardColor} text-white text-xs font-semibold`}
                          >
                            {getInitials(person.first_name, person.last_name)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {props.toastLinkingAvailable && props.posProvider && (
                              <Tooltip
                                title={
                                  hasToastLink
                                    ? "Linked to POS employee"
                                    : "No POS employee linked"
                                }
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    props.onOpenToastLink?.(
                                      { ...person, is_archived: true },
                                      {
                                        onRequestRefresh: props.refreshEmployees,
                                      }
                                    )
                                  }
                                  disabled={!props.onOpenToastLink}
                                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1"
                                  aria-label="Manage POS link"
                                >
                                  {hasToastLink ? (
                                    <LinkIcon
                                      fontSize="inherit"
                                      className="text-emerald-600"
                                    />
                                  ) : (
                                    <LinkOffIcon
                                      fontSize="inherit"
                                      className="text-slate-400"
                                    />
                                  )}
                                </button>
                              </Tooltip>
                            )}
                            <span>{formatName(person.first_name)}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          {formatName(person.last_name)}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center space-x-2 text-slate-900">
                              <EmailIcon style={{ fontSize: "16px" }} className="text-slate-400" />
                              <span>{person.email || "No email"}</span>
                            </div>
                            {person.phone_number && (
                              <div className="flex items-center space-x-2 text-slate-600 text-xs">
                                <PhoneIcon style={{ fontSize: "14px" }} className="text-slate-400" />
                                <span>{formatPhoneNumber(person.phone_number)}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600">
                          <div className="flex flex-col">
                            <span>
                              {person.archived_at
                                ? new Date(person.archived_at).toLocaleDateString()
                                : person.updated_at
                                ? new Date(person.updated_at).toLocaleDateString()
                                : "N/A"}
                            </span>
                            {person.archived_by_name && (
                              <span className="text-gray-500 text-xs mt-0.5">
                                by {person.archived_by_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-600">
                          {person.archive_reason || "N/A"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {person.rehire_eligible !== undefined ? (
                            <span
                              className={`px-2 py-1 rounded-md text-xs font-medium ${
                                person.rehire_eligible
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {person.rehire_eligible ? "Yes" : "No"}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 text-xs font-medium">
                              Yes
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap py-4 pl-3 pr-6 text-center">
                          <LoadingButton
                            variant="outlined"
                            startIcon={<RestoreIcon />}
                            onClick={() => {
                              props.clone_or_reactivate_store_team_member(
                                person.id,
                                props.homeStore,
                                person.permission_type
                              );
                            }}
                            sx={{
                              textTransform: "none",
                              borderColor: "#10b981",
                              color: "#10b981",
                              "&:hover": {
                                backgroundColor: "#d1fae5",
                                borderColor: "#059669",
                              },
                            }}
                          >
                            Reactivate
                          </LoadingButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
